import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Calendar, Eye, Lightbulb, Send, ArrowRight, Facebook, MapPin } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_post-planner-51/artifacts/17buinbo_AFA%20transparent.png';

const WHAT_YOU_GET = [
  '12 months of social media content, planned, created, and posted for you',
  'Original captions written in your brand\'s voice',
  'Professional images selected specifically for your business',
  'Scheduled publishing so posts go out on the right days, automatically',
  'A private review portal to approve posts or request changes from your phone',
  'An easy way to share ideas. Submit a thought and we turn it into content',
];

const THIS_IS = [
  'A done-for-you Facebook posting service',
  'Consistent, professional content on your page every week',
  'One less thing on your plate',
];

const THIS_IS_NOT = [
  'A social media agency. We don\'t run ads, respond to comments, or manage your inbox',
  'A DIY tool you have to learn. We handle everything behind the scenes',
  'A commitment to every platform. We focus on Facebook image posts, and we do them well',
];

const STEPS = [
  {
    num: '01',
    title: 'We learn your business',
    desc: 'Your vibe, your audience, your busy seasons.',
    icon: Lightbulb,
    color: '#01939E',
  },
  {
    num: '02',
    title: 'We build your calendar',
    desc: '12 months of posts, ready for your review.',
    icon: Calendar,
    color: '#7ED321',
  },
  {
    num: '03',
    title: 'You approve (or tweak)',
    desc: 'Through a simple link we send you. No app needed.',
    icon: Eye,
    color: '#01939E',
  },
  {
    num: '04',
    title: 'We publish',
    desc: 'Your posts go live on schedule, every week.',
    icon: Send,
    color: '#7ED321',
  },
];

const SLIDER_STOPS = [
  {
    min: 0, max: 15,
    label: 'We never post. Like, ever.',
    feedback: 'That\'s exactly who this is for.',
    detail: 'We\'ll build your entire social presence from scratch. 12 months of content, done for you.',
    color: '#DC2626',
    intensity: 'perfect',
  },
  {
    min: 16, max: 35,
    label: 'Maybe a few times a year?',
    feedback: 'You could really use this.',
    detail: 'A few posts a year isn\'t enough to stay visible. We\'ll turn that into a consistent weekly presence.',
    color: '#EA580C',
    intensity: 'great',
  },
  {
    min: 36, max: 55,
    label: 'We try, but it\'s inconsistent.',
    feedback: 'Sound familiar? Let us take it from here.',
    detail: 'No more guilt about forgetting to post. We\'ll keep your page active so you can focus on what you do best.',
    color: '#EAB308',
    intensity: 'good',
  },
  {
    min: 56, max: 75,
    label: 'We post regularly, but it eats up time.',
    feedback: 'Imagine getting those hours back.',
    detail: 'You know it works. You just wish someone else could handle it. That\'s literally what we do.',
    color: '#84CC16',
    intensity: 'moderate',
  },
  {
    min: 76, max: 90,
    label: 'We have a good routine going.',
    feedback: 'Nice! You\'re ahead of most businesses.',
    detail: 'If you ever want to hand it off or take a break, we\'re here. Your content quality won\'t skip a beat.',
    color: '#22C55E',
    intensity: 'light',
  },
  {
    min: 91, max: 100,
    label: 'We have someone on it several times a week.',
    feedback: 'You\'re covered, but we\'re here if that changes.',
    detail: 'If your person moves on or you want to reallocate that time, we can step in seamlessly.',
    color: '#16A34A',
    intensity: 'minimal',
  },
];

function AssessmentSlider() {
  const [value, setValue] = useState(25);

  const currentStop = useMemo(
    () => SLIDER_STOPS.find((s) => value >= s.min && value <= s.max) || SLIDER_STOPS[0],
    [value]
  );

  const showCta = value <= 75;
  const pct = value;

  return (
    <section
      className="py-16 sm:py-20 border-b border-gray-100"
      data-testid="hiw-assessment-slider"
    >
      <div className="max-w-2xl mx-auto text-center mb-10">
        <p className="text-[#01939E] text-xs font-bold uppercase tracking-[0.2em] mb-3">
          Quick Check
        </p>
        <h2 className="text-lg font-bold text-[#003C59] uppercase tracking-wider mb-2">
          Is This Right for You?
        </h2>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          Drag the slider to describe your current Facebook activity.
        </p>
      </div>

      <div className="max-w-xl mx-auto px-6">
        {/* Slider Track */}
        <div className="relative pt-2 pb-8">
          {/* Full traffic-light gradient background */}
          <div
            className="relative h-3 rounded-full overflow-hidden"
            style={{
              background: 'linear-gradient(90deg, #DC2626 0%, #EA580C 20%, #EAB308 40%, #84CC16 60%, #22C55E 80%, #16A34A 100%)',
              opacity: 0.2,
            }}
          />
          {/* Filled portion up to thumb */}
          <div
            className="absolute top-2 left-0 h-3 rounded-full transition-all duration-150"
            style={{
              width: `${pct}%`,
              background: `linear-gradient(90deg, #DC2626 0%, #EA580C ${Math.min(100, 2000/Math.max(pct,1))}%, #EAB308 ${Math.min(100, 4000/Math.max(pct,1))}%, #84CC16 ${Math.min(100, 6000/Math.max(pct,1))}%, #22C55E ${Math.min(100, 8000/Math.max(pct,1))}%, #16A34A 100%)`,
            }}
          />

          {/* Custom range input */}
          <input
            type="range"
            min="0"
            max="100"
            value={value}
            onChange={(e) => setValue(Number(e.target.value))}
            className="assessment-slider absolute inset-0 w-full h-3 opacity-0 cursor-pointer"
            style={{ top: '8px' }}
            data-testid="hiw-assessment-range"
          />

          {/* Thumb indicator */}
          <div
            className="absolute top-0 w-7 h-7 rounded-full bg-white border-[3px] shadow-lg pointer-events-none transition-all duration-150"
            style={{
              left: `calc(${pct}% - 14px)`,
              borderColor: currentStop.color,
              top: '1px',
            }}
          />

          {/* Endpoint labels */}
          <div className="flex justify-between mt-4">
            <span className="text-[11px] text-gray-400 max-w-[120px] leading-tight">
              We almost never post
            </span>
            <span className="text-[11px] text-gray-400 max-w-[140px] leading-tight text-right">
              Someone manages it several times a week
            </span>
          </div>
        </div>

        {/* Dynamic Feedback Card */}
        <div
          className="mt-6 rounded-2xl p-6 transition-all duration-300 border"
          style={{
            backgroundColor: currentStop.color + '08',
            borderColor: currentStop.color + '20',
          }}
        >
          {/* Current position label */}
          <p className="text-xs font-medium text-gray-400 mb-1 uppercase tracking-wider">
            You said:
          </p>
          <p
            className="text-sm font-semibold mb-4 transition-colors duration-300"
            style={{ color: currentStop.color }}
            data-testid="hiw-assessment-label"
          >
            "{currentStop.label}"
          </p>

          {/* Feedback */}
          <p
            className="text-xl sm:text-2xl font-bold text-[#003C59] mb-2 transition-all duration-300"
            data-testid="hiw-assessment-feedback"
          >
            {currentStop.feedback}
          </p>
          <p className="text-sm text-gray-500 leading-relaxed">
            {currentStop.detail}
          </p>

          {/* CTA for good-fit users */}
          {showCta && (
            <div className="mt-5 pt-4 border-t" style={{ borderColor: currentStop.color + '15' }}>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-lg shadow-md transition-all hover:shadow-lg"
                style={{ backgroundColor: currentStop.color }}
                data-testid="hiw-assessment-cta"
              >
                See How We Can Help
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="how-it-works-page">

      {/* Sales Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Anne Flinn Advertising" className="h-10 w-auto" />
            <span className="text-xs text-[#003C59]/50 font-medium italic hidden sm:inline">Developed for the small business, by Anne.</span>
          </Link>
          <div className="flex items-center gap-5">
            <span
              className="text-sm font-semibold text-[#003C59] border-b-2 border-[#01939E] pb-0.5"
              data-testid="hiw-nav-active"
            >
              How It Works
            </span>
            <Link
              to="/pricing"
              className="text-sm font-medium text-[#003C59]/60 hover:text-[#01939E] transition-colors"
              data-testid="hiw-nav-pricing"
            >
              Pricing
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-[#003C59] text-white py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#7ED321] text-xs font-bold uppercase tracking-[0.2em] mb-4">
            How It Works
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Your Social Media,{' '}
            <span className="text-[#01939E]">Simplified.</span>
          </h1>
          <p className="text-[#A2D0D5] text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            You're busy running a business. You don't have time to think about what to post, find the right photo, write a caption, and remember to hit "publish" three times a week. That's where we come in.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6">

        {/* Assessment Slider */}
        <AssessmentSlider />

        {/* What You Get */}
        <section className="py-16 sm:py-20">
          <h2 className="text-lg font-bold text-[#003C59] uppercase tracking-wider mb-8 text-center">
            What You Get
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {WHAT_YOU_GET.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-4 rounded-xl bg-[#f0fafa] border border-[#01939E]/10"
              >
                <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-[#01939E] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
                <p className="text-sm text-[#003C59] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How It Works Steps */}
        <section className="py-16 sm:py-20 border-t border-gray-100">
          <h2 className="text-lg font-bold text-[#003C59] uppercase tracking-wider mb-12 text-center">
            Four Simple Steps
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step) => (
              <div key={step.num} className="text-center group">
                <div
                  className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center transition-transform group-hover:scale-110"
                  style={{ backgroundColor: step.color + '15' }}
                >
                  <step.icon className="w-6 h-6" style={{ color: step.color }} />
                </div>
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.2em] block mb-1"
                  style={{ color: step.color }}
                >
                  Step {step.num}
                </span>
                <h3 className="text-base font-bold text-[#003C59] mb-1">{step.title}</h3>
                <p className="text-sm text-gray-500">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-base font-semibold text-[#003C59]">
              That's it. You go back to running your business.
            </p>
          </div>
        </section>

        {/* Real Examples */}
        <section className="py-16 sm:py-20 border-t border-gray-100">
          <h2 className="text-lg font-bold text-[#003C59] uppercase tracking-wider mb-3 text-center">
            Real Examples
          </h2>
          <p className="text-sm text-gray-500 text-center mb-10 max-w-md mx-auto">
            Here are actual posts we created and published for a client, from photo selection to caption to hashtags.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <div>
              <div className="rounded-2xl overflow-hidden shadow-xl shadow-black/10 border border-gray-200">
                <img
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_post-planner-51/artifacts/9c68a4427fd76849_IMG_7843.webp"
                  alt="Example Facebook post for Bella Terra Landscapes, a poll asking followers about their dream outdoor space"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <p className="text-[11px] text-gray-400 text-center mt-3 italic">Engagement poll post</p>
            </div>
            <div>
              <div className="rounded-2xl overflow-hidden shadow-xl shadow-black/10 border border-gray-200">
                <img
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_post-planner-51/artifacts/3e7124896e08b0a3_IMG_7844.webp"
                  alt="Example Facebook post for Bella Terra Landscapes, Welcome October with autumn inspiration and fall landscape photos"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <p className="text-[11px] text-gray-400 text-center mt-3 italic">Seasonal inspiration post</p>
            </div>
          </div>
          <p className="text-xs text-gray-400 text-center mt-6 italic">
            Bella Terra Landscapes. Written, designed, and published by our team.
          </p>
        </section>

        {/* Your Facebook Stays Yours */}
        <section className="py-16 sm:py-20 border-t border-gray-100" data-testid="hiw-fb-insights-section">
          <h2 className="text-lg font-bold text-[#003C59] uppercase tracking-wider mb-3 text-center">
            Your Facebook Stays Yours
          </h2>
          <p className="text-sm text-gray-500 text-center mb-10 max-w-lg mx-auto">
            We publish content on your behalf, but nothing else changes. You keep full access to your page, your insights, your messages, and your audience, exactly the way it's always been.
          </p>
          <div className="max-w-3xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-xl shadow-black/10 border border-gray-200">
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_post-planner-51/artifacts/c039fb5d20a3449f_Screenshot%202026-10-09%20at%2011.59.29%E2%80%AFAM.png"
                alt="Facebook Page Insights dashboard showing views, engagement analytics, and recent posts, all unchanged and fully accessible to the business owner"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
            <p className="text-[11px] text-gray-400 text-center mt-3 italic">
              A real client's Facebook Insights. All analytics, controls, and admin access remain with you.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto mt-10">
            <div className="text-center p-4 rounded-xl bg-[#1877F2]/5 border border-[#1877F2]/10">
              <p className="text-sm font-bold text-[#003C59]">Your Page</p>
              <p className="text-xs text-gray-500 mt-1">You stay the admin. Always.</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-[#1877F2]/5 border border-[#1877F2]/10">
              <p className="text-sm font-bold text-[#003C59]">Your Insights</p>
              <p className="text-xs text-gray-500 mt-1">Views, reach, followers. All yours.</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-[#1877F2]/5 border border-[#1877F2]/10">
              <p className="text-sm font-bold text-[#003C59]">Your Inbox</p>
              <p className="text-xs text-gray-500 mt-1">Messages and comments stay with you.</p>
            </div>
          </div>
        </section>

        {/* Value of No-Engagement Posts */}
        <section className="py-16 sm:py-20 border-t border-gray-100" data-testid="hiw-no-engagement-value">
          <div className="max-w-2xl mx-auto">
            <p className="text-[#01939E] text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center">
              But What If Nobody Likes My Posts?
            </p>
            <h2 className="text-lg font-bold text-[#003C59] uppercase tracking-wider mb-4 text-center">
              The Value of "No-Engagement" Posts
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed text-center max-w-xl mx-auto mb-10">
              Posting on Facebook without engagement still builds passive visibility and trust. Many users silently watch or contact businesses later without ever liking or commenting. If you have no time, pouring hours into unengaged posts is rarely worth it unless you change how you use the platform.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="p-5 rounded-2xl bg-[#003C59]/[0.03] border border-[#003C59]/10">
                <div className="w-10 h-10 rounded-xl bg-[#003C59]/10 flex items-center justify-center mb-3">
                  <Eye className="w-5 h-5 text-[#003C59]" />
                </div>
                <h3 className="text-sm font-bold text-[#003C59] mb-1.5">Digital Billboard</h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  People look up a business page before buying or visiting. An active profile proves you're still open and operational.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-[#003C59]/[0.03] border border-[#003C59]/10">
                <div className="w-10 h-10 rounded-xl bg-[#003C59]/10 flex items-center justify-center mb-3">
                  <svg className="w-5 h-5 text-[#003C59]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <h3 className="text-sm font-bold text-[#003C59] mb-1.5">Silent Buyers</h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Many customers browse, remember your name, and message or call later without ever hitting "like" or leaving a comment.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-[#003C59]/[0.03] border border-[#003C59]/10">
                <div className="w-10 h-10 rounded-xl bg-[#003C59]/10 flex items-center justify-center mb-3">
                  <MapPin className="w-5 h-5 text-[#003C59]" />
                </div>
                <h3 className="text-sm font-bold text-[#003C59] mb-1.5">Local SEO & Info Hub</h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Your Facebook page acts as a mini-website displaying your hours, address, phone number, and customer reviews.
                </p>
              </div>
            </div>

            <p className="text-[11px] text-gray-400 text-center mt-6">
              That's why we handle the posting, so you get the visibility without spending the time.
            </p>
          </div>
        </section>

        {/* This Is / This Is Not */}
        <section className="py-16 sm:py-20 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* This Is */}
            <div className="p-6 rounded-2xl bg-[#f0fafa] border border-[#01939E]/15">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-[#01939E] flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                <h3 className="font-bold text-[#003C59] text-base">This Is</h3>
              </div>
              <ul className="space-y-3">
                {THIS_IS.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" strokeWidth={2.5} />
                    <span className="text-sm text-[#003C59]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* This Is Not */}
            <div className="p-6 rounded-2xl bg-gray-50 border border-gray-200">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-gray-300 flex items-center justify-center">
                  <X className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                <h3 className="font-bold text-[#003C59] text-base">This Is Not</h3>
              </div>
              <ul className="space-y-3">
                {THIS_IS_NOT.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <X className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" strokeWidth={2.5} />
                    <span className="text-sm text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Currently Posting */}
        <section className="py-12 sm:py-16 border-t border-gray-100">
          <div className="max-w-md mx-auto text-center p-8 rounded-2xl bg-[#1877F2]/5 border border-[#1877F2]/15">
            <Facebook className="w-10 h-10 text-[#1877F2] mx-auto mb-4" />
            <h3 className="font-bold text-[#003C59] text-base mb-2">Currently Posting On</h3>
            <p className="text-2xl font-bold text-[#1877F2] mb-2">Facebook</p>
            <p className="text-sm text-gray-500">
              Image posts, published on a consistent schedule to keep your business visible. More platforms coming soon.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-gray-100 text-center">
          <p className="text-2xl font-bold text-[#003C59] mb-3">
            Take <span className="italic">"what should I post?"</span> off your list.
          </p>
          <p className="text-gray-500 text-base mb-8 max-w-lg mx-auto">
            No learning curve, no extra staff, no guessing. You approve everything. Anne handles the rest.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#01939E] hover:bg-[#017a83] text-white font-semibold rounded-lg shadow-lg shadow-[#01939E]/20 transition-all text-base"
            data-testid="hiw-cta-btn"
          >
            See Plans & Get Started
            <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-gray-400 mt-3">No contracts. Cancel anytime.</p>
        </section>

      </div>

      {/* Footer */}
      <footer className="bg-[#003C59] text-white/50 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="" className="h-8 w-auto opacity-50" />
          <div className="flex items-center gap-6 text-xs">
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/how-it-works" className="hover:text-white transition-colors">How It Works</Link>
          </div>
          <p className="text-xs">Anne Flinn Advertising</p>
        </div>
      </footer>
    </div>
  );
}
